/**
 * Floating bubbles: the Google rating badge (Google & YouTube app) and the Shopify Inbox chat button.
 *
 * Both apps pin themselves to the bottom corners of the screen. This module never touches what they
 * render and never moves their DOM. It only measures, then hands numbers to CSS (assets/cfs-global.css, floating part):
 *   - level: the badge's centre is lined up with the chat button's centre, on every page and screen size
 *   - lift: both rise above the product page's sticky add bar and above the footer's bottom line
 *     (copyright / payment marks), so they never cover either
 *   - hide: on the cart page both are hidden while the checkout summary (desktop: the Checkout button) sits in their
 *     corner, and on the mobile product page while the quantity / Add to basket row (and the "Pay
 *     securely" line under it) is in their corner, e.g. before the shopper scrolls (owner, 9 Oct 2026)
 *     (class cfs-float-hidden on <html>)
 *
 * How each is moved:
 *   - Chat: Inbox's own public variable, --shopify-chat-activator-offset, set on the <shopify-chat> host.
 *   - Badge: Google's script writes `bottom` and `left` inline with !important, which no stylesheet can
 *     beat, so the badge is moved with the independent `translate` property (fed by --cfs-badge-x and
 *     --cfs-badge-y, set on the badge wrapper).
 * Both variables are written on those two elements, not on <html>: the lift changes on every scroll frame
 * near the footer, and a custom property changed on <html> re-computes the style of the whole page.
 *
 * Contract with the rest of the theme:
 *   - the product page's sticky add bar is [data-cfs-sticky-add]; it must have no height while it is
 *     not shown (hidden or display: none). Its real height is measured here, never hard-coded.
 *   - the footer's bottom line is [data-cfs-footer-line] (falls back to .cfs-footer__bottom): the row whose
 *     top edge is the line above the copyright and payment marks. The 72px above it stays empty, so the
 *     bubbles can rest there between the Visit block and the line.
 */

const CHAT_HEIGHT = 50; // Inbox button at its 16px font: 46px + (16px - 13px) * 1.4
const SIDE_INSET = 20; // Inbox sits 20px from the right edge; the badge mirrors it on the left
const PILL_INSET = 16; // Google draws its pill 16px inside the wrapper's left edge
const PILL_DROP = 2; // ...and its centre about 2px above the wrapper's centre (measured on the live badge)
const GAP = 12; // clear space kept between a bubble and the sticky bar or footer line
const ZONE = 80; // height of the bottom corner a sticky checkout must clear before the bubbles may show
const CARD_HEIGHT = 110; // a badge wrapper taller than this is the opened Google rating card
const BADGE_ID = 'google-merchantwidget-iframe-wrapper';

const root = document.documentElement;
const narrow = window.matchMedia('(max-width: 989px)');

// Inbox's resting offset comes from its embed's :root rule ("Button vertical position" setting).
const restValue = parseFloat(getComputedStyle(root).getPropertyValue('--shopify-chat-activator-offset'));
const rest = restValue > 0 ? restValue : 20;

/** @type {HTMLElement | null} */
let chat = null;
/** @type {HTMLElement | null} */
let bar = null;
/** @type {HTMLElement | null} */
let footerLine = null;
/** @type {HTMLElement | null} */
let badge = null;
let onCartPage = false;
/** @type {HTMLElement | null} the product page's own quantity / Add to basket row */
let buyRow = null;
/** @type {HTMLElement | null} the "Pay securely with" line under it */
let payLine = null;
// From 750px up the page scrolls inside .page-wrapper (html and body are overflow: hidden); below that the window scrolls.
/** @type {Element | null} */
let pageWrapper = null;

let shiftX = 0;
let shiftY = 0;
let cardOpen = false;
let badgeSignature = '';
let queued = false;
let measureDue = true;

const observer = new ResizeObserver(() => queue());
// Google rewrites the wrapper's inline style (its size, and bottom/left when the window crosses 600px).
// Our own variables land in the same attribute, so only a change to Google's properties counts.
const badgeStyleObserver = new MutationObserver(() => {
  if (!badge) return;
  const { bottom, left, width, height } = badge.style;
  const signature = [bottom, left, width, height].join('|');
  if (signature === badgeSignature) return;
  badgeSignature = signature;
  queue();
});

/** Writes a custom property on an element only when its value changes (null removes it). */
function setVar(/** @type {HTMLElement | null} */ element, /** @type {string} */ name, /** @type {string | null} */ value) {
  if (!element || element.style.getPropertyValue(name) === (value ?? '')) return;
  if (value === null) element.style.removeProperty(name);
  else element.style.setProperty(name, value);
}

/** Reads where Google put the badge and works out the shift that levels it with the chat button. */
function measureBadge() {
  const height = badge ? badge.offsetHeight : 0;
  cardOpen = height > CARD_HEIGHT;
  if (!badge || !height || cardOpen) return; // not laid out yet, or the rating card is open: keep the last shift
  const style = getComputedStyle(badge);
  const left = parseFloat(style.left) || 0;
  const centre = (parseFloat(style.bottom) || 0) + height / 2 + PILL_DROP; // pill centre, measured up from the screen bottom
  shiftY = Math.round(centre - (rest + CHAT_HEIGHT / 2)); // positive = move down
  // A badge placed on the right (Google & YouTube "Widget position") is left alone sideways.
  shiftX = left < window.innerWidth / 2 ? Math.round(SIDE_INSET - (left + PILL_INSET)) : 0; // positive = move right
}

function update() {
  queued = false;

  // Google adds its wrapper a few seconds after load and may replace it.
  const found = /** @type {HTMLElement | null} */ (document.getElementById(BADGE_ID));
  if (found !== badge) {
    badge = found;
    measureDue = true;
    badgeSignature = '';
    badgeStyleObserver.disconnect();
    if (found) {
      observer.observe(found);
      badgeStyleObserver.observe(found, { attributes: true, attributeFilter: ['style'] });
    }
  }
  if (measureDue) {
    measureDue = false;
    measureBadge();
  }

  const viewport = window.innerHeight;
  let lift = 0;
  if (bar) {
    const box = bar.getBoundingClientRect();
    if (box.height) lift = Math.max(lift, viewport - box.top + GAP - rest); // 0 height = bar not shown
  }
  if (footerLine) {
    const box = footerLine.getBoundingClientRect();
    if ((box.height || box.width) && box.top < viewport) lift = Math.max(lift, viewport - box.top + GAP - rest);
  }
  lift = Math.max(0, Math.round(lift));

  let hidden = false;
  if (onCartPage && narrow.matches) {
    const box = document.querySelector('.cfs-cart-page .cfs-basket__summary')?.getBoundingClientRect();
    hidden = Boolean(box && box.height && box.bottom > viewport - ZONE && box.top < viewport);
  } else if (onCartPage) {
    // Desktop: the summary column starts beside the heading, so Checkout can sit in the chat's corner
    const box = document.querySelector('.cfs-cart-page [data-cfs-checkout]')?.getBoundingClientRect();
    hidden = Boolean(box && box.height && box.bottom > viewport - ZONE && box.top < viewport);
  }
  if (!hidden && buyRow && narrow.matches) {
    const row = buyRow.getBoundingClientRect();
    const bottom = Math.max(row.bottom, payLine?.getBoundingClientRect().bottom ?? 0);
    hidden = Boolean(row.height && bottom > viewport - ZONE - lift && row.top < viewport - lift);
  }

  setVar(chat, '--shopify-chat-activator-offset', lift ? `${rest + lift}px` : null);
  // The rating card is anchored by Google: while it is open the badge keeps only its levelling shift.
  setVar(badge, '--cfs-badge-x', `${shiftX}px`);
  setVar(badge, '--cfs-badge-y', `${shiftY - (cardOpen ? 0 : lift)}px`);
  root.classList.toggle('cfs-float-hidden', hidden);
}

/** Schedules one update on the next frame. Scrolling passes false: nothing about the badge itself changes. */
function queue(measure = true) {
  if (measure) measureDue = true;
  if (queued) return;
  queued = true;
  requestAnimationFrame(update);
}

/** Finds the chat, sticky bar, footer line and cart page, and watches the ones that can change size. */
function connect() {
  chat = document.querySelector('shopify-chat');
  bar = document.querySelector('[data-cfs-sticky-add]');
  footerLine = document.querySelector('[data-cfs-footer-line], .cfs-footer__bottom');
  onCartPage = Boolean(document.querySelector('.cfs-cart-page'));
  buyRow = document.querySelector('[data-cfs-main-actions]');
  payLine = document.querySelector('.cfs-product__pay');
  pageWrapper = document.querySelector('.page-wrapper');

  // Content growing or shrinking moves the footer and the cart summary without a scroll event. On desktop
  // the body is a fixed 100% tall, so watch the main content and the footer line as well. The sticky
  // add bar appearing or disappearing changes its size from or to 0.
  observer.disconnect();
  for (const element of [document.body, document.getElementById('MainContent'), footerLine, bar, badge]) {
    if (element) observer.observe(element);
  }
  queue();
}

// Only the page itself matters: ignore the scroll events of carousels and other inner scrollers.
document.addEventListener('scroll', (event) => (event.target === document || event.target === pageWrapper) && queue(false), {
  passive: true,
  capture: true,
});
window.addEventListener('resize', () => queue());
window.addEventListener('pageshow', () => queue());
narrow.addEventListener('change', () => queue());

// Theme editor: sections re-render without a page load, so look for the bar and footer line again.
document.addEventListener('shopify:section:load', connect);
document.addEventListener('shopify:section:unload', connect);

// Google's wrapper arrives a few seconds after load and may be replaced: pick it up as soon as <body> gets a new child.
new MutationObserver(() => queue()).observe(document.body, { childList: true });

connect();
